import { useState } from 'react'
import { Stack, Text, Box, Group, Button, Alert, Skeleton, Tabs, SimpleGrid } from '@mantine/core'
import { IconPlayerPlay, IconShieldLock, IconChartLine, IconTargetArrow } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentTenant } from '@/store/auth'
import { securityApi } from '@/api/security'
import { formatApiError } from '@/api/errors'
import { WidgetCard } from '@/components/ui'
import { FindingDrawer } from '@/components/security/FindingDrawer'
import { ScanProgressModal } from './components/ScanProgress/ScanProgressModal'
import { PostureStrip } from './components/Posture/PostureStrip'
import { OwaspHeatmap } from './components/Posture/OwaspHeatmap'
import { FilaDeCorrecao } from './components/Posture/FilaDeCorrecao'
import { PentestPanel } from './components/Pentest/PentestPanel'
import { TrendChart } from './components/Evolution/TrendChart'
import { ScanTimeline } from './components/Evolution/ScanTimeline'
import type { SecurityFinding } from '@/api/security'

const STATUS_SCAN: Record<string, string> = {
  completed: 'concluído',
  running: 'em andamento',
  failed: 'falhou',
  pending: 'na fila',
}

export function SecurityOverview() {
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const qc = useQueryClient()
  const [selectedFinding, setSelectedFinding] = useState<SecurityFinding | null>(null)
  const [drawerOpened, setDrawerOpened] = useState(false)
  const [scanModalOpen, setScanModalOpen] = useState(false)
  const [activeScanId, setActiveScanId] = useState<number | null>(null)
  const [catFilter, setCatFilter] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: ['security', 'overview', tenantId],
    queryFn: () => securityApi.getOverview(tenantId!),
    enabled: !!tenantId,
  })

  const trend = useQuery({
    queryKey: ['security', 'trend', tenantId],
    queryFn: () => securityApi.getTrend(tenantId!),
    enabled: !!tenantId,
  })

  const triggerScan = useMutation({
    mutationFn: () => {
      if (!tenantId) return Promise.reject(new Error('No tenant selected'))
      return securityApi.triggerScan(tenantId)
    },
    onSuccess: (data: any) => {
      const scanId = data?.scan_id
      if (scanId) {
        setActiveScanId(scanId)
        setScanModalOpen(true)
      }
      qc.invalidateQueries({ queryKey: ['security'] })
    },
    onError: (err: any) => {
      alert(`Erro ao iniciar scan: ${formatApiError(err, 'Erro desconhecido')}`)
    },
  })

  if (!tenantId) return <Alert color="yellow">Selecione um tenant para visualizar a segurança.</Alert>

  const data = overview.data
  const isLoading = overview.isLoading
  const categories = data?.categories ?? []
  const trendPoints = trend.data?.points ?? []

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Button
          leftSection={<IconPlayerPlay size={16} />}
          color="teal"
          loading={triggerScan.isPending}
          onClick={() => triggerScan.mutate()}
        >
          Iniciar Scan
        </Button>
        {data?.last_scan && (
          <Text size="sm" c="dimmed">
            Último scan: {STATUS_SCAN[data.last_scan.status] ?? data.last_scan.status}
            {data.last_scan.completed_at ? ` — ${new Date(data.last_scan.completed_at).toLocaleString('pt-BR')}` : ''}
          </Text>
        )}
      </Group>

      {overview.isError ? (
        <Alert color="red">{formatApiError(overview.error, 'Falha ao carregar a postura')}</Alert>
      ) : (
        <Tabs defaultValue="postura">
          <Tabs.List>
            <Tabs.Tab value="postura" leftSection={<IconShieldLock size={16} />}>Postura</Tabs.Tab>
            <Tabs.Tab value="evolucao" leftSection={<IconChartLine size={16} />}>Evolução</Tabs.Tab>
            <Tabs.Tab value="pentest" leftSection={<IconTargetArrow size={16} />}>Pentest</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="postura" pt="md">
            {isLoading ? (
              <Stack gap="md">
                <Skeleton height={56} />
                <Skeleton height={160} />
                <Skeleton height={200} />
              </Stack>
            ) : (
              <Stack gap="lg">
                <PostureStrip
                  criticalCount={data?.critical_count ?? 0}
                  openCount={data?.open_count ?? 0}
                  hint="clique numa categoria para filtrar a fila ↓"
                />

                <Box>
                  <Text size="sm" fw={600} c="#098d93" tt="uppercase" mb="sm"
                        style={{ letterSpacing: '0.06em' }}>
                    OWASP Top 10 — findings abertos por categoria (cor = pior severidade)
                  </Text>
                  <OwaspHeatmap
                    categories={categories}
                    onSelect={(id) => setCatFilter((cur) => (cur === id ? null : id))}
                  />
                </Box>

                <WidgetCard>
                  <FilaDeCorrecao
                    tenantId={tenantId}
                    categoria={catFilter}
                    onLimparCategoria={() => setCatFilter(null)}
                    onSelect={(f) => { setSelectedFinding(f); setDrawerOpened(true) }}
                  />
                </WidgetCard>
              </Stack>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="evolucao" pt="md">
            {trend.isLoading ? (
              <Skeleton height={300} />
            ) : trend.isError ? (
              <Alert color="red">{formatApiError(trend.error, 'Falha ao carregar a evolução')}</Alert>
            ) : (
              <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
                <WidgetCard>
                  <Text size="sm" fw={600} mb="md">Curva da postura ao longo dos scans</Text>
                  <TrendChart points={trendPoints} />
                </WidgetCard>
                <WidgetCard>
                  <Text size="sm" fw={600} mb="md">Linha do tempo de scans — delta vs. o anterior</Text>
                  <ScanTimeline points={trendPoints} />
                </WidgetCard>
              </SimpleGrid>
            )}
          </Tabs.Panel>

          <Tabs.Panel value="pentest" pt="md">
            <PentestPanel
              tenantId={tenantId}
              onSelect={(f) => { setSelectedFinding(f); setDrawerOpened(true) }}
            />
          </Tabs.Panel>
        </Tabs>
      )}

      <FindingDrawer
        finding={selectedFinding}
        opened={drawerOpened}
        onClose={() => { setDrawerOpened(false); setSelectedFinding(null) }}
      />

      <ScanProgressModal
        opened={scanModalOpen}
        scanId={activeScanId}
        tenantId={tenantId}
        onClose={() => { setScanModalOpen(false); setActiveScanId(null) }}
        onComplete={() => qc.invalidateQueries({ queryKey: ['security'] })}
      />
    </Stack>
  )
}
